import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useQuery } from "react-query";

import {
    getCombinedCustomChartsData,
    getCognitiveLoadBreakdownData,
    getCognitiveLoadTimelineData,
    getCrewCognitiveLoadData,
    getCustomChartsData,
    getDashboardData,
    getFlightCircuitData,
    getGazeData,
    getEyeTrackingContainerData,
    getDigitalSignatureExposureData,
    getEyeTrackingCanvasData,
    getMapData,
    getResultsData,
    getScoreData,
    getCognitiveLoadWithStatsData,
    getColorPhaseScoreData,
    getSubDimensionsData,
    getOverallPhaseScoreData,
    getCombinedCustomTopStatsData,
    getStatsSummaryWithDropdownData,
    getStatsSummaryData,
    getCircuitStatsData,
    getCycleContainerData,
    getMultiFlightCircuitData,
    getPhaseSummaryData,
    getLineProcessData,
} from "@/api/sessions";
import { getCustomerSettings, getLayoutGrid } from "@/api/settings";

import type { CrewFilterOption } from "@/pages/session-detail/components/crew-cognitive-load/crew-cognitive-load.types";

import type { Client } from "@/enums/clients";

import useQueryParams from "@/utils/use-query-params";

//context type
interface SessionDetailsType {
    //PageLayout
    isFetchingGridLayout: boolean;
    gridLayoutData: any;
    isFetchedGridLayout: boolean;

    //CombinedCustomCharts
    isFetchingCombinedCustomCharts: boolean;
    combinedCustomChartsData: any;
    isErrorCombinedCustomCharts: boolean;

    //CustomCharts
    isFetchingCustomCharts: boolean;
    customChartsData: any;
    isErrorCustomCharts: boolean;

    //CombinedCustomTopStats
    isFetchingCombinedCustomTopStats: boolean;
    combinedCustomTopStatsData: any;
    isErrorCombinedCustomTopStats: boolean;

    //RecognitionScore, ProgressScore
    isFetchingScore: boolean;
    scoreData: any;
    isErrorScore: boolean;

    //Results
    isFetchingResults: boolean;
    resultsData: any;
    isErrorResults: boolean;

    //CrewCognitiveLoad
    isFetchingCrewCognitiveLoad: boolean;
    crewCognitiveLoadData: any;
    isErrorCrewCognitiveLoad: boolean;

    //CognitiveLoadBreakdown
    isFetchingCognitiveLoadBreakdown: boolean;
    cognitiveLoadBreakdownData: any;
    isErrorCognitiveLoadBreakdown: boolean;

    //CognitiveLoadTimeline
    isFetchingCognitiveLoadTimeline: boolean;
    cognitiveLoadTimelineData: any;
    isErrorCognitiveLoadTimeline: boolean;

    //CognitiveLoadWithStats
    isFetchingCognitiveLoadWithStats: boolean;
    cognitiveLoadWithStatsData: any;
    isErrorCognitiveLoadWithStats: boolean;

    //ColorPhaseScore
    colorPhaseScoreData: any;
    isFetchingColorPhaseScore: boolean;
    isErrorColorPhaseScore: boolean;

    //SubDimensions - FireMissions, FlyingCircuits, Intercept
    subDimensionsData: any;
    isFetchingSubDimensions: boolean;
    isErrorSubDimensions: boolean;

    //OverallPhaseScore
    overallPhaseScoreData: any;
    isFetchingOverallPhaseScore: boolean;
    isErrorOverallPhaseScore: boolean;

    //StatsSummaryWithDropdown
    statsSummaryWithDropdownData: any;
    isFetchingStatsSummaryWithDropdown: boolean;
    isErrorStatsSummaryWithDropdown: boolean;

    //StatsSummary - // Endpoint to get all stats summary components data, except CircuitStats
    statsSummaryData: any;
    isFetchingStatsSummary: boolean;
    isErrorStatsSummary: boolean;

    //StatsSummaryWithModalReport
    isTargetFilterOpen: boolean;
    setIsTargetFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
    selectedTarget: string | null;
    setSelectedTarget: React.Dispatch<React.SetStateAction<string | null>>;
    targetFilterOptions: string[] | null;
    setTargetFilterOptions: React.Dispatch<React.SetStateAction<string[] | null>>;
    selectedTargetIndex: number;
    setSelectedTargetIndex: React.Dispatch<React.SetStateAction<number>>;

    //CircuitStats
    circuitStatsData: any;
    isFetchingCircuitStats: boolean;
    isErrorCircuitStats: boolean;

    //MapTimeline
    isFetchingMapData: boolean;
    mapTimelineData: any;
    isErrorMapData: boolean;
    currentTick: number;
    setTick: Dispatch<SetStateAction<number>>;
    isPlayingMapTimeline: boolean;
    playMapTimeline: () => void;
    pauseMapTimeline: () => void;
    resetMapTimeline: () => void;

    //CustomerSettings
    isFetchingCustomerSettings: boolean;
    customerSettingsData: any;
    customerName: Client;
    isErrorCustomerSettings: boolean;

    //RAPIDD Flying Circuits - rendered by ProgressScore
    isCircuitFilterOpen: boolean;
    setIsCircuitFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
    circuitFilterOptions: string[] | null;
    setCircuitFilterOptions: React.Dispatch<React.SetStateAction<string[] | null>>;
    selectedCircuit: string | null;
    setSelectedCircuit: React.Dispatch<React.SetStateAction<string | null>>;
    selectedCircuitIndex: number;
    setSelectedCircuitIndex: React.Dispatch<React.SetStateAction<number>>;

    //CrewCognitiveLoad
    isCrewFilterOpen: boolean;
    setIsCrewFilterOpen: React.Dispatch<React.SetStateAction<any | null>>;
    crewFilterOptions: CrewFilterOption[] | null;
    setCrewFilterOptions: React.Dispatch<React.SetStateAction<CrewFilterOption[] | null>>;
    selectedCrewFilterOption: CrewFilterOption | null;
    setSelectedCrewFilterOption: React.Dispatch<React.SetStateAction<CrewFilterOption | null>>;
    selectedCrewIndex: number;
    setSelectedCrewIndex: React.Dispatch<React.SetStateAction<number>>;

    //FireMissionModal - rendered by ResponseTime
    isFireMissionFilterOpen: boolean;
    setIsFireMissionFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
    fireMissionFilterOptions: string[] | null;
    setFireMissionFilterOptions: React.Dispatch<React.SetStateAction<string[] | null>>;
    selectedFireMission: string | null;
    setSelectedFireMission: React.Dispatch<React.SetStateAction<string | null>>;
    selectedFireMissionIndex: number | null;
    setSelectedFireMissionIndex: React.Dispatch<React.SetStateAction<number | null>>;

    //Flying Circuit
    flightCircuitData: any;
    isFetchingFlightCircuit: boolean;
    isErrorFlightCircuit: boolean;

    // Dashboard (single fetch per session; ImpactHeatmap from payload.ImpactHeatmap)
    dashboardData: any;
    isFetchingDashboardData: boolean;
    isErrorDashboardData: boolean;
    impactHeatmapData: any;
    barChartPayload: any;
    //Eye Tracking Container
    eyeTrackingContainerData: any;
    isFetchingEyeTrackingContainer: boolean;
    isErrorEyeTrackingContainer: boolean;

    //Eye Tracking Canvas
    eyeTrackingCanvasData: any;
    isFetchingEyeTrackingCanvas: boolean;
    isErrorEyeTrackingCanvas: boolean;

    //Gaze Data (for EyeTrackingCanvas inside EyeTrackingCanvasXCognitiveLoad)
    gazeData: any;
    isFetchingGazeData: boolean;
    isErrorGazeData: boolean;

    //DigitalSignatureExposure
    digitalSignatureExposureData: any;
    isFetchingDigitalSignatureExposure: boolean;
    isErrorDigitalSignatureExposure: boolean;

    //CycleContainer
    isFetchingCycleContainer: boolean;
    cycleContainerData: any;
    isErrorCycleContainer: boolean;

    //MultiFlightCircuit
    multiFlightCircuitData: any;
    isFetchingMultiFlightCircuit: boolean;
    isErrorMultiFlightCircuit: boolean;

    //PhaseSummary
    phaseSummaryData: any;
    isFetchingPhaseSummary: boolean;
    isErrorPhaseSummary: boolean;

    //LineProcess
    lineProcessData: any;
    isFetchingLineProcess: boolean;
    isErrorLineProcess: boolean;
}

interface LayoutGridProps {
    simulationId: string;
    scenarioInstanceId: string;
}

interface CombinedCustomChartsProps {
    combinedCustomChartsData: any;
    isFetchingCombinedCustomCharts: boolean;
    isErrorCombinedCustomCharts: boolean;
    customerName?: string;
}

interface CombinedCustomTopStatsProps {
    combinedCustomTopStatsData: any;
    isFetchingCombinedCustomTopStats: boolean;
    isErrorCombinedCustomTopStats: boolean;
}

interface ScoreDataProps {
    recognitionScoreData: any;
    isFetchingRecognitionScore: boolean;
    isErrorRecognitionScore: boolean;
}

interface CustomChartsDataProps {
    customChartsData: any;
    isFetching: boolean;
}

interface ResultsDataProps {
    resultsData: any;
    isFetching: boolean;
}

interface CognitiveLoadBreakdownProps {
    cognitiveLoadBreakdownData: any;
}

interface CognitiveLoadTimelineProps {
    cognitiveLoadTimelineData: any;
}

interface CognitiveLoadWithStatsProps {
    cognitiveLoadWithStatsData: any;
}

interface ColorPhaseScoreProps {
    phaseScoringData: any;
    isFetching: boolean;
    isError: boolean;
}

interface CrewCognitiveLoadProps {
    crewCognitiveLoadData: any;
    isFetching: boolean;
}

interface SubDimensionsProps {
    subDimensionsData: any;
    isFetchingSubDimensionsData: boolean;
    isErrorSubDimensionsData: boolean;
}

interface OverallPhaseScoreProps {
    overallPhaseScoreData: any;
    isFetchingOverallPhaseScore: boolean;
    isErrorOverallPhaseScore: boolean;
}

interface StatsSummaryWithDropdownProps {
    statsSummaryWithDropdownData: any;
    isFetchingStatsSummaryWithDropdown: boolean;
    isErrorStatsSummaryWithDropdown: boolean;
}

interface StatsSummaryProps {
    statsSummaryData: any;
    isFetchingStatsSummary: boolean;
    isErrorStatsSummary: boolean;
}

interface CircuitStatsProps {
    circuitStatsData: any;
    isFetchingCircuitStats: boolean;
    isErrorCircuitStats: boolean;
}

interface MapDataProps {
    simulationId: string;
    scenarioInstanceId: string;
    start: string;
    end: string;
}

interface CustomerSettingsDataProps {
    customerName: string;
    emailProvider: string;
    scenarioImageUrl: string;
    simLogoUrl: string;
}

interface FlightCircuitProps {
    flightCircuitData: any;
    isFetchingFlightCircuit: boolean;
    isErrorFlightCircuit: boolean;
}

interface EyeTrackingContainerProps {
    eyeTrackingContainerData: any;
    isFetchingEyeTrackingContainer: boolean;
    isErrorEyeTrackingContainer: boolean;
}

interface EyeTrackingCanvasProps {
    eyeTrackingCanvasData: any;
    isFetchingEyeTrackingCanvas: boolean;
    isErrorEyeTrackingCanvas: boolean;
}

interface GazeDataProps {
    gazeData: any;
    isFetchingGazeData: boolean;
    isErrorGazeData: boolean;
}

interface DigitalSignatureExposureProps {
    digitalSignatureExposureData: any;
    isFetchingDigitalSignatureExposure: boolean;
    isErrorDigitalSignatureExposure: boolean;
}

interface CycleContainerProps {
    data: any;
    isFetchingCycleContainer: boolean;
    isErrorCycleContainer: boolean;
}

interface MultiFlightCircuitProps {
    multiFlightCircuitData: any;
    isFetchingMultiFlightCircuit: boolean;
    isErrorMultiFlightCircuit: boolean;
}

interface PhaseSummaryProps {
    phaseSummaryData: any;
    isFetchingPhaseSummary: boolean;
    isErrorPhaseSummary: boolean;
}

interface LineProcessProps {
    phaseSummaryData: any;
    isFetchingPhaseSummary: boolean;
    isErrorPhaseSummary: boolean;
}

//create the context
const SessionDetailsContext = createContext<SessionDetailsType | undefined>(undefined);

//create the provider
export const SessionDetailsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    //flying-circuits-modal-content
    const [isCircuitFilterOpen, setIsCircuitFilterOpen] = useState<boolean>(false);
    const [circuitFilterOptions, setCircuitFilterOptions] = useState<string[] | null>(null);
    const [selectedCircuit, setSelectedCircuit] = useState<string | null>(null);
    const [selectedCircuitIndex, setSelectedCircuitIndex] = useState<number>(0);

    const [isCrewFilterOpen, setIsCrewFilterOpen] = useState<boolean>(false);
    const [crewFilterOptions, setCrewFilterOptions] = useState<CrewFilterOption[] | null>(null);
    const [selectedCrewFilterOption, setSelectedCrewFilterOption] = useState<any | null>(null);
    const [selectedCrewIndex, setSelectedCrewIndex] = useState<number>(0);

    const [isFireMissionFilterOpen, setIsFireMissionFilterOpen] = useState<boolean>(false);
    const [fireMissionFilterOptions, setFireMissionFilterOptions] = useState<string[] | null>(null);
    const [selectedFireMission, setSelectedFireMission] = useState<string | null>(null);
    const [selectedFireMissionIndex, setSelectedFireMissionIndex] = useState<number | null>(null);

    //StatsSummaryWithModalReport
    const [isTargetFilterOpen, setIsTargetFilterOpen] = useState<boolean>(false);
    const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
    const [targetFilterOptions, setTargetFilterOptions] = useState<any | null>(null);
    const [selectedTargetIndex, setSelectedTargetIndex] = useState<number>(0);

    const [currentTick, setTick] = useState<number>(0); // Current tick index MapTimeline component
    const [isPlayingMapTimeline, setIsPlayingMapTimeline] = useState<boolean>(false);

    const isSessionDetailsPage = window.location.pathname.includes("session-analysis");
    const isQueryEnabled = isSessionDetailsPage;

    const params: any = useQueryParams();
    const userId = params.get("userId");
    const from: string = params.get("from");
    const to: string = params.get("to");
    const simulationId: string = params.get("simulationInstanceId");
    const scenarioInstanceId: string = params.get("scenarioInstanceId");
    const start = params.get("from") || "";
    const end = params.get("to") || "";

    //PageLayout
    //API request: /page-layout
    const {
        isFetching: isFetchingGridLayout,
        data: gridLayoutData,
        isFetched: isFetchedGridLayout,
    } = useQuery<LayoutGridProps>(
        ["getLayoutGrid", simulationId, scenarioInstanceId],
        () => getLayoutGrid({ simulationId, scenarioInstanceId }),
        {
            suspense: true, //has to be true in order for the components to render,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: !!simulationId || !!scenarioInstanceId,
        },
    );

    //CombinedCustomCharts
    //API request: /combined-custom-charts
    //EyeTracking, CockpitTimeline, TimelineChartDriving & TimelineChartFiring components
    //TimelineChartFlight uses a separate API call to CombinedCharts (through usePoll function)
    const {
        isFetching: isFetchingCombinedCustomCharts,
        data: combinedCustomChartsData,
        isError: isErrorCombinedCustomCharts,
    } = useQuery<CombinedCustomChartsProps>(
        ["getCombinedCustomChartsData", from, to, userId, simulationId, scenarioInstanceId],
        () =>
            getCombinedCustomChartsData({
                from,
                to,
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //CombinedCustomTopStats
    //API request: /combined-custom-top-stats
    const {
        isFetching: isFetchingCombinedCustomTopStats,
        data: combinedCustomTopStatsData,
        isError: isErrorCombinedCustomTopStats,
    } = useQuery<CombinedCustomTopStatsProps>(
        ["getCombinedCustomTopStatsData", userId, from, to, simulationId, scenarioInstanceId],
        () =>
            getCombinedCustomTopStatsData({
                from,
                to,
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //RecognitionScore, ProgressScore, ProgressScoreTable
    //API request: /Scoring
    const {
        isFetching: isFetchingScore,
        data: scoreData,
        isError: isErrorScore,
    } = useQuery<ScoreDataProps>(
        ["getScoreData", from, to, userId, simulationId, scenarioInstanceId],
        () =>
            getScoreData({
                from,
                to,
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //CustomCharts
    //API request: /custom-charts
    //CognitiveLoad(SemiCircularGauge), FireMission & OverallScore components
    //LandingScore component uses a separate API call to CustomChartsData (through usePoll function)
    const {
        isFetching: isFetchingCustomCharts,
        data: customChartsData,
        isError: isErrorCustomCharts,
    } = useQuery<CustomChartsDataProps>(
        ["getCustomChartsData", from, to, userId, simulationId, scenarioInstanceId],
        () =>
            getCustomChartsData({
                from,
                to,
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //Results
    //API request: /results
    const {
        isFetching: isFetchingResults,
        data: resultsData,
        isError: isErrorResults,
    } = useQuery<ResultsDataProps>(
        ["getResultsData", userId, simulationId, scenarioInstanceId],
        () =>
            getResultsData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //CrewCognitiveLoad
    //API request: /crew-cognitive-load
    const {
        isFetching: isFetchingCrewCognitiveLoad,
        data: crewCognitiveLoadData,
        isError: isErrorCrewCognitiveLoad,
    } = useQuery<CrewCognitiveLoadProps>(
        ["getCrewCognitiveLoadData", userId, simulationId, scenarioInstanceId],
        () =>
            getCrewCognitiveLoadData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //CognitiveLoadBreakdown
    //API request: /cognitive-load-breakdown
    const {
        isFetching: isFetchingCognitiveLoadBreakdown,
        data: cognitiveLoadBreakdownData,
        isError: isErrorCognitiveLoadBreakdown,
    } = useQuery<CognitiveLoadBreakdownProps>(
        ["getCognitiveLoadBreakdownData", userId, simulationId, scenarioInstanceId],
        () =>
            getCognitiveLoadBreakdownData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //CognitiveLoadTimeline
    //API request: /cognitive-load-timeline
    const {
        isFetching: isFetchingCognitiveLoadTimeline,
        data: cognitiveLoadTimelineData,
        isError: isErrorCognitiveLoadTimeline,
    } = useQuery<CognitiveLoadTimelineProps>(
        ["getCognitiveLoadTimelineData", userId, simulationId, scenarioInstanceId],
        () =>
            getCognitiveLoadTimelineData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //CognitiveLoadWithStats
    //API request: /cognitive-load-with-stats
    const {
        isFetching: isFetchingCognitiveLoadWithStats,
        data: cognitiveLoadWithStatsData,
        isError: isErrorCognitiveLoadWithStats,
    } = useQuery<CognitiveLoadWithStatsProps>(
        ["getCognitiveLoadWithStatsData", userId, simulationId, scenarioInstanceId],
        () =>
            getCognitiveLoadWithStatsData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //ColorPhaseScore
    //API request: /phase-scoring
    const {
        isFetching: isFetchingColorPhaseScore,
        data: colorPhaseScoreData,
        isError: isErrorColorPhaseScore,
    } = useQuery<ColorPhaseScoreProps>(
        ["getColorPhaseScoreData", userId, simulationId, scenarioInstanceId],
        () =>
            getColorPhaseScoreData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //SubDimensions - FireMissions, FlyingCircuits, Intercept
    //API request: /sub-dimensions
    const {
        isFetching: isFetchingSubDimensions,
        data: subDimensionsData,
        isError: isErrorSubDimensions,
    } = useQuery<SubDimensionsProps>(
        ["getSubDimensionsData", userId, simulationId, scenarioInstanceId],
        () =>
            getSubDimensionsData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //OverallPhaseScore
    //API request: /overall-phase-score
    const {
        isFetching: isFetchingOverallPhaseScore,
        data: overallPhaseScoreData,
        isError: isErrorOverallPhaseScore,
    } = useQuery<OverallPhaseScoreProps>(
        ["getOverallPhaseScoreData", userId, simulationId, scenarioInstanceId],
        () =>
            getOverallPhaseScoreData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //StatsSummaryWithDropdown
    //API request: /stats-summary-with-dropdown
    const {
        isFetching: isFetchingStatsSummaryWithDropdown,
        data: statsSummaryWithDropdownData,
        isError: isErrorStatsSummaryWithDropdown,
    } = useQuery<StatsSummaryWithDropdownProps>(
        ["getStatsSummaryWithDropdownData", userId, simulationId, scenarioInstanceId],
        () =>
            getStatsSummaryWithDropdownData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //StatsSummary
    //API request: /stats-summary
    const {
        isFetching: isFetchingStatsSummary,
        data: statsSummaryData,
        isError: isErrorStatsSummary,
    } = useQuery<StatsSummaryProps>(
        ["getStatsSummaryData", userId, simulationId, scenarioInstanceId],
        () =>
            getStatsSummaryData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //CircuitStats
    //API request: /circuit-stats
    const {
        isFetching: isFetchingCircuitStats,
        data: circuitStatsData,
        isError: isErrorCircuitStats,
    } = useQuery<CircuitStatsProps>(
        ["getCircuitStatsData", userId, simulationId, scenarioInstanceId],
        () =>
            getCircuitStatsData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //MapTimeline
    //API request: /Map
    const {
        data: mapTimelineData,
        isError: isErrorMapData,
        isFetching: isFetchingMapData,
    } = useQuery<MapDataProps>(
        ["mapData", simulationId, scenarioInstanceId, start, end],
        () => getMapData(simulationId, scenarioInstanceId, 0, 10000),
        {
            suspense: false,
            refetchOnMount: false,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    // Function to start MapTimeline playback
    const playMapTimeline = useCallback(() => {
        setIsPlayingMapTimeline(true);
    }, []);

    // Function to pause MapTimeline playback
    const pauseMapTimeline = useCallback(() => {
        setIsPlayingMapTimeline(false);
    }, []);

    // Function to reset MapTimeline playback to the initial state
    const resetMapTimeline = useCallback(() => {
        setTick(currentTick); // Reset the tick index to the initial value
        setIsPlayingMapTimeline(false); // Ensure the playback is paused
    }, [currentTick]);

    // CustomerSettingsData
    // API request: /settings/customer-settings
    const {
        isFetching: isFetchingCustomerSettings,
        data: customerSettingsData,
        isError: isErrorCustomerSettings,
    } = useQuery<CustomerSettingsDataProps>(["getCustomerSettings"], getCustomerSettings, {
        suspense: false,
        refetchOnMount: false,
        useErrorBoundary: false,
        enabled: isQueryEnabled,
    });

    //Flying Circuits
    //API request: /flight-circuit
    const {
        isFetching: isFetchingFlightCircuit,
        data: flightCircuitData,
        isError: isErrorFlightCircuit,
    } = useQuery<FlightCircuitProps>(
        ["getFlightCircuitData", userId, simulationId, scenarioInstanceId],
        () =>
            getFlightCircuitData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //Eye Tracking Container
    //API request: /eye-tracking-data
    const {
        isFetching: isFetchingEyeTrackingContainer,
        data: eyeTrackingContainerData,
        isError: isErrorEyeTrackingContainer,
    } = useQuery<EyeTrackingContainerProps>(
        ["getEyeTrackingContainerData", userId, simulationId, scenarioInstanceId],
        () =>
            getEyeTrackingContainerData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //Eye Tracking Canvas
    //API request: /eye-tracking-canvas
    const {
        isFetching: isFetchingEyeTrackingCanvas,
        data: eyeTrackingCanvasData,
        isError: isErrorEyeTrackingCanvas,
    } = useQuery<EyeTrackingCanvasProps>(
        ["getEyeTrackingCanvasData", userId, simulationId, scenarioInstanceId],
        () =>
            getEyeTrackingCanvasData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //Gaze Data (for EyeTrackingCanvas inside EyeTrackingCanvasXCognitiveLoad)
    //API request: /gaze-data
    const {
        isFetching: isFetchingGazeData,
        data: gazeData,
        isError: isErrorGazeData,
    } = useQuery<GazeDataProps>(
        ["getGazeData", userId, simulationId, scenarioInstanceId],
        () =>
            getGazeData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //DigitalSignatureExposure
    //API request: /digital-signature-exposure
    const {
        isFetching: isFetchingDigitalSignatureExposure,
        data: digitalSignatureExposureData,
        isError: isErrorDigitalSignatureExposure,
    } = useQuery<DigitalSignatureExposureProps>(
        ["getDigitalSignatureExposureData", userId, simulationId, scenarioInstanceId],
        () =>
            getDigitalSignatureExposureData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //CycleContainer
    //API request: /cycle-container
    const {
        isFetching: isFetchingCycleContainer,
        data: cycleContainerData,
        isError: isErrorCycleContainer,
    } = useQuery<CycleContainerProps>(
        ["getCycleContainerData", userId, simulationId, scenarioInstanceId],
        () =>
            getCycleContainerData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //MultiFlightCircuit
    //API request: /multi-flight-circuit
    const {
        isFetching: isFetchingMultiFlightCircuit,
        data: multiFlightCircuitData,
        isError: isErrorMultiFlightCircuit,
    } = useQuery<MultiFlightCircuitProps>(
        ["getMultiFlightCircuitData", userId, simulationId, scenarioInstanceId],
        () =>
            getMultiFlightCircuitData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    //PhaseSummary
    //API request: /phase-summary
    const {
        isFetching: isFetchingPhaseSummary,
        data: phaseSummaryData,
        isError: isErrorPhaseSummary,
    } = useQuery<PhaseSummaryProps>(
        ["getPhaseSummaryData", userId, simulationId, scenarioInstanceId],
        () =>
            getPhaseSummaryData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );

    // Dashboard payload (one fetch per session); ImpactHeatmap when present
    const {
        isFetching: isFetchingDashboardData,
        data: dashboardData,
        isError: isErrorDashboardData,
    } = useQuery(
        ["getDashboardData", scenarioInstanceId],
        () =>
            getDashboardData({
                dimensionId: scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled && !!scenarioInstanceId,
        },
    );

    const impactHeatmapData = useMemo(() => {
        const heatmap = dashboardData?.ImpactHeatmap;

        if (!heatmap?.data?.points) return undefined;

        return {
            data: heatmap.data,
            title: heatmap.title,
            legendType: heatmap.legendType,
            showAxisLabels: heatmap.showAxisLabels,
        };
    }, [dashboardData]);

    const barChartPayload = useMemo(() => {
        return dashboardData?.BarChart;
    }, [dashboardData]);

    //LineProcess
    //API request: /line-process
    const {
        isFetching: isFetchingLineProcess,
        data: lineProcessData,
        isError: isErrorLineProcess,
    } = useQuery<LineProcessProps>(
        ["getLineProcessData", userId, simulationId, scenarioInstanceId],
        () =>
            getLineProcessData({
                userId,
                simulationInstanceId: simulationId,
                scenarioInstanceId,
            }),
        {
            suspense: false,
            refetchOnMount: true,
            useErrorBoundary: false,
            enabled: isQueryEnabled,
        },
    );
    const customerName = useMemo(() => {
        return customerSettingsData?.customerName as Client;
    }, [isFetchingCustomerSettings, customerSettingsData]);

    return (
        <SessionDetailsContext.Provider
            value={{
                //PageLayout
                isFetchingGridLayout,
                gridLayoutData,
                isFetchedGridLayout,

                //CombinedCustomCharts
                isFetchingCombinedCustomCharts,
                combinedCustomChartsData,
                isErrorCombinedCustomCharts,

                //CombinedCustomTopStats
                isFetchingCombinedCustomTopStats,
                combinedCustomTopStatsData,
                isErrorCombinedCustomTopStats,

                //CustomCharts
                isFetchingCustomCharts,
                customChartsData,
                isErrorCustomCharts,

                //RecognitionScore, ProgressScore
                isFetchingScore,
                scoreData,
                isErrorScore,

                //Results
                isFetchingResults,
                resultsData,
                isErrorResults,

                //CrewCognitiveLoad
                isFetchingCrewCognitiveLoad,
                crewCognitiveLoadData,
                isErrorCrewCognitiveLoad,

                //CognitiveLoadBreakdown
                isFetchingCognitiveLoadBreakdown,
                cognitiveLoadBreakdownData,
                isErrorCognitiveLoadBreakdown,

                //CognitiveLoadTimeline
                isFetchingCognitiveLoadTimeline,
                cognitiveLoadTimelineData,
                isErrorCognitiveLoadTimeline,

                //CognitiveLoadWithStats
                isFetchingCognitiveLoadWithStats,
                cognitiveLoadWithStatsData,
                isErrorCognitiveLoadWithStats,

                //ColorPhaseScore
                isFetchingColorPhaseScore,
                colorPhaseScoreData,
                isErrorColorPhaseScore,

                //SubDimensions - FireMissions, FlyingCircuits, Intercept
                subDimensionsData,
                isFetchingSubDimensions,
                isErrorSubDimensions,

                //OverallPhaseScore
                isFetchingOverallPhaseScore,
                overallPhaseScoreData,
                isErrorOverallPhaseScore,

                //StatsSummaryWithDropdown
                statsSummaryWithDropdownData,
                isFetchingStatsSummaryWithDropdown,
                isErrorStatsSummaryWithDropdown,

                //StatsSummary
                statsSummaryData,
                isFetchingStatsSummary,
                isErrorStatsSummary,

                //StatsSummaryWithModalReport
                isTargetFilterOpen,
                setIsTargetFilterOpen,

                selectedTarget,
                setSelectedTarget,

                targetFilterOptions,
                setTargetFilterOptions,

                selectedTargetIndex,
                setSelectedTargetIndex,

                //CircuitStats
                isFetchingCircuitStats,
                circuitStatsData,
                isErrorCircuitStats,

                //MapTimeline
                isFetchingMapData,
                mapTimelineData,
                isErrorMapData,
                currentTick,
                setTick,
                isPlayingMapTimeline,
                playMapTimeline,
                pauseMapTimeline,
                resetMapTimeline,

                //CustomerSettings
                isFetchingCustomerSettings,
                customerSettingsData,
                customerName,
                isErrorCustomerSettings,

                //Flying Circuits - rendered by ProgressScore
                isCircuitFilterOpen,
                setIsCircuitFilterOpen,
                circuitFilterOptions,
                setCircuitFilterOptions,
                selectedCircuitIndex,
                selectedCircuit,
                setSelectedCircuit,
                setSelectedCircuitIndex,

                //CrewCognitiveLoad - rendered by CrewCognitiveLoad
                isCrewFilterOpen,
                setIsCrewFilterOpen,
                crewFilterOptions,
                setCrewFilterOptions,
                selectedCrewFilterOption,
                setSelectedCrewFilterOption,
                selectedCrewIndex,
                setSelectedCrewIndex,

                //FireMissionModal - rendered by ResponseTime
                isFireMissionFilterOpen,
                setIsFireMissionFilterOpen,
                fireMissionFilterOptions,
                setFireMissionFilterOptions,
                selectedFireMission,
                setSelectedFireMission,
                selectedFireMissionIndex,
                setSelectedFireMissionIndex,

                //FlightPath - 3D
                flightCircuitData,
                isFetchingFlightCircuit,
                isErrorFlightCircuit,

                // Dashboard / ImpactHeatmap
                dashboardData,
                isFetchingDashboardData,
                isErrorDashboardData,
                impactHeatmapData,
                barChartPayload,
                //Eye Tracking Container
                eyeTrackingContainerData,
                isFetchingEyeTrackingContainer,
                isErrorEyeTrackingContainer,

                //Eye Tracking Canvas
                eyeTrackingCanvasData,
                isFetchingEyeTrackingCanvas,
                isErrorEyeTrackingCanvas,

                //Gaze Data (for EyeTrackingCanvas inside EyeTrackingCanvasXCognitiveLoad)
                gazeData,
                isFetchingGazeData,
                isErrorGazeData,

                //DigitalSignatureExposure
                digitalSignatureExposureData,
                isFetchingDigitalSignatureExposure,
                isErrorDigitalSignatureExposure,

                //CycleContainer
                isFetchingCycleContainer,
                cycleContainerData,
                isErrorCycleContainer,

                //MultiFlightCircuit
                multiFlightCircuitData,
                isFetchingMultiFlightCircuit,
                isErrorMultiFlightCircuit,

                //PhaseSummary
                phaseSummaryData,
                isFetchingPhaseSummary,
                isErrorPhaseSummary,

                //LineProcess
                isFetchingLineProcess,
                lineProcessData,
                isErrorLineProcess,
            }}
        >
            {children}
        </SessionDetailsContext.Provider>
    );
};

//custom hook to export context's values & functions
export const useSessionDetailsContext = (): SessionDetailsType => {
    const context = useContext(SessionDetailsContext);

    if (!context) {
        throw new Error("useSessionDetails hook must be used within a ModalProvider");
    }

    return context;
};
